<template>
  <div class="bereiche-requests-view container py-4">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2>
          <font-awesome-icon icon="clipboard-list" class="me-2" />
          Meine Beitrittsanfragen
        </h2>
        <p class="text-muted mb-0">Übersicht über alle Ihre Bereich-Beitrittsanfragen</p>
      </div>
      <BackButton fallback="teams-list" />
    </div>

    <!-- Filter und Suche -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <label for="statusFilter" class="form-label">Status</label>
            <select 
              id="statusFilter" 
              class="form-select" 
              v-model="filters.status"
              @change="loadRequests"
            >
              <option value="">Alle Status</option>
              <option value="pending">Ausstehend</option>
              <option value="approved">Genehmigt</option>
              <option value="rejected">Abgelehnt</option>
            </select>
          </div>
          <div class="col-md-4">
            <label for="searchInput" class="form-label">Bereich suchen</label>
            <div class="input-group">
              <input
                id="searchInput"
                type="text"
                class="form-control"
                placeholder="Bereich-Name oder Beschreibung..."
                v-model="filters.search"
                @input="debounceSearch"
              />
              <button 
                class="btn btn-outline-secondary" 
                type="button"
                @click="clearSearch"
                v-if="filters.search"
              >
                <font-awesome-icon icon="times" />
              </button>
            </div>
          </div>
          <div class="col-md-4">
            <label for="limitSelect" class="form-label">Einträge pro Seite</label>
            <select 
              id="limitSelect" 
              class="form-select" 
              v-model="pagination.limit"
              @change="changeLimit"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Lade Anfragen...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" /> {{ error }}
    </div>

    <!-- Empty State -->
    <div v-else-if="requests.length === 0" class="card">
      <div class="card-body text-center py-5">
        <font-awesome-icon icon="clipboard-list" class="fa-3x mb-3 text-muted" />
        <h5>Keine Beitrittsanfragen gefunden</h5>
        <p class="text-muted">
          {{ filters.status || filters.search ? 'Keine Anfragen entsprechen den aktuellen Filterkriterien.' : 'Du hast noch keine Bereich-Beitrittsanfragen gestellt.' }}
        </p>
        <div class="mt-3">
          <button class="btn btn-primary" @click="goToJoin">
            <font-awesome-icon icon="user-plus" class="me-2" />
            Bereich beitreten
          </button>
        </div>
      </div>
    </div>

    <!-- Requests List -->
    <div v-else class="table-responsive">
      <table class="table table-hover">
        <thead>
          <tr>
            <th>Bereich</th>
            <th>Einladungscode</th>
            <th>Angefragt am</th>
            <th>Status</th>
            <th>Bearbeitet</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="request in requests" :key="request.id">
            <td>
              <div>
                <strong>{{ request.invitation?.Team?.name || 'Unbekannter Bereich' }}</strong>
                <br>
                <small class="text-muted">{{ request.invitation?.Team?.description || 'Keine Beschreibung verfügbar.' }}</small>
              </div>
            </td>
            <td>
              <code>{{ request.invitation?.code || '-' }}</code>
            </td>
            <td>{{ formatDate(request.requestedAt) }}</td>
            <td>
              <span class="badge" :class="getStatusBadgeClass(request.status)">
                {{ getStatusLabel(request.status) }}
              </span>
            </td>
            <td>
              <span v-if="request.processedAt">
                {{ formatDate(request.processedAt) }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>
              <div class="d-flex gap-1">
                <button
                  v-if="request.status === 'pending'"
                  class="btn btn-sm btn-outline-danger"
                  @click="cancelRequest(request)"
                  title="Anfrage abbrechen"
                >
                  <font-awesome-icon icon="times" />
                </button>
                <button
                  v-else-if="request.status === 'rejected'"
                  class="btn btn-sm btn-outline-primary"
                  @click="retryRequest(request)"
                  title="Erneut versuchen"
                >
                  <font-awesome-icon icon="redo" />
                </button>
                <button
                  v-if="request.status === 'rejected' && request.rejectionReason"
                  class="btn btn-sm btn-outline-info"
                  @click="showRejectionReason(request)"
                  title="Ablehnungsgrund anzeigen"
                >
                  <font-awesome-icon icon="eye" />
                </button>
                <button
                  v-if="request.status === 'approved'"
                  class="btn btn-sm btn-success"
                  @click="goToTeam(request.invitation?.Team?.id)"
                  title="Bereich anzeigen"
                >
                  <font-awesome-icon icon="external-link-alt" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <nav v-if="pagination.totalPages > 1" aria-label="Paginierung der Beitrittsanfragen">
        <ul class="pagination justify-content-center">
          <li class="page-item" :class="{ disabled: pagination.page === 1 }">
            <button class="page-link" @click="changePage(1)" :disabled="pagination.page === 1">
              <font-awesome-icon icon="angle-double-left" />
            </button>
          </li>
          <li class="page-item" :class="{ disabled: pagination.page === 1 }">
            <button class="page-link" @click="changePage(pagination.page - 1)" :disabled="pagination.page === 1">
              <font-awesome-icon icon="angle-left" />
            </button>
          </li>
          
          <li 
            v-for="page in getPageNumbers()" 
            :key="page" 
            class="page-item" 
            :class="{ active: page === pagination.page }"
          >
            <button class="page-link" @click="changePage(page)">{{ page }}</button>
          </li>
          
          <li class="page-item" :class="{ disabled: pagination.page === pagination.totalPages }">
            <button class="page-link" @click="changePage(pagination.page + 1)" :disabled="pagination.page === pagination.totalPages">
              <font-awesome-icon icon="angle-right" />
            </button>
          </li>
          <li class="page-item" :class="{ disabled: pagination.page === pagination.totalPages }">
            <button class="page-link" @click="changePage(pagination.totalPages)" :disabled="pagination.page === pagination.totalPages">
              <font-awesome-icon icon="angle-double-right" />
            </button>
          </li>
        </ul>
      </nav>
      
      <!-- Pagination Info -->
      <div class="text-center text-muted small">
        Zeige {{ ((pagination.page - 1) * pagination.limit) + 1 }} bis 
        {{ Math.min(pagination.page * pagination.limit, pagination.total) }} von 
        {{ pagination.total }} Einträgen
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { teamService } from '@/api/teams'
import { showSuccess, showError, showConfirmDialog, showRejectionReasonModal } from '@/services/notification'

const router = useRouter()


const loading = ref(false)
const error = ref(null)
const requests = ref([])
const searchTimeout = ref(null)


const filters = ref({
  status: '',
  search: ''
})


const pagination = ref({
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0
})


const loadRequests = async () => {
  loading.value = true
  error.value = null
  
  try {
    const result = await teamService.getUserJoinRequests(
      filters.value.status || null,
      pagination.value.page,
      pagination.value.limit,
      filters.value.search
    )
    
    requests.value = result.requests || []
    pagination.value = { ...pagination.value, ...result.pagination }
  } catch (err) {
    console.error('Fehler beim Laden der Anfragen:', err)
    error.value = 'Anfragen konnten nicht geladen werden'
  } finally {
    loading.value = false
  }
}


const debounceSearch = () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }
  
  searchTimeout.value = setTimeout(() => {
    pagination.value.page = 1
    loadRequests()
  }, 500)
}


const clearSearch = () => {
  filters.value.search = ''
  pagination.value.page = 1
  loadRequests()
}


const changeLimit = () => {
  pagination.value.page = 1
  loadRequests()
}


const changePage = (page) => {
  if (page >= 1 && page <= pagination.value.totalPages) {
    pagination.value.page = page
    loadRequests()
  }
}


const getPageNumbers = () => {
  const current = pagination.value.page
  const total = pagination.value.totalPages
  const pages = []

  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1)
  }

  if (current <= 3) {
    return [1, 2, 3, 4, 5]
  }

  if (current >= total - 2) {
    return [total - 4, total - 3, total - 2, total - 1, total]
  }

  return [current - 2, current - 1, current, current + 1, current + 2]
}


const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}




const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'approved':
      return 'bg-success'
    case 'rejected':
      return 'bg-danger'
    case 'pending':
      return 'bg-warning text-dark'
    default:
      return 'bg-secondary'
  }
}


const getStatusLabel = (status) => {
  switch (status) {
    case 'approved':
      return 'Genehmigt'
    case 'rejected':
      return 'Abgelehnt'
    case 'pending':
      return 'Ausstehend'
    default:
      return 'Unbekannt'
  }
}


const cancelRequest = async (request) => {
  const result = await showConfirmDialog(
    'Anfrage abbrechen',
    `Möchtest du deine Beitrittsanfrage für den Bereich "${request.invitation?.Team?.name}" wirklich abbrechen?`,
    'Ja, abbrechen',
    'Nein'
  )

  if (result.isConfirmed) {
    try {
      await teamService.cancelJoinRequest(request.id)
      showSuccess('Beitrittsanfrage wurde erfolgreich abgebrochen')
      await loadRequests()
    } catch (err) {
      console.error('Fehler beim Abbrechen der Anfrage:', err)
      showError('Anfrage konnte nicht abgebrochen werden')
    }
  }
}


const showRejectionReason = async (request) => {
    const teamName = request.invitation?.Team?.name || 'Unbekannter Bereich'
  const rejectedAt = request.processedAt ? formatDate(request.processedAt) : null
  
  await showRejectionReasonModal(`Bereich "${teamName}"`, request.rejectionReason, rejectedAt)
}


const retryRequest = (request) => {
  if (request.invitation?.code) {
    router.push({ 
      name: 'join-with-code', 
      params: { code: request.invitation.code } 
    })
  } else {
    showError('Einladungscode nicht verfügbar')
  }
}


const goBack = () => {
  router.push({ name: 'teams-list' })
}

const goToJoin = () => {
  router.push({ name: 'teams-join' })
}

const goToTeam = (teamId) => {
  if (teamId) {
    router.push({ name: 'teams-detail', params: { id: teamId } })
  }
}


onMounted(() => {
  loadRequests()
})
</script>
